// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState } from 'react';
import { KPICard } from '../../Results/ResultsTiles';
import { formatEurosCompact, type VaccineOutputs } from '../lib/models';
import type { DashboardSettings } from '../../../store/vaccineDashboardStore';
import { DashboardSettingsModal } from './DashboardSettingsModal';
import { useVaccineDashboardStore } from '../../../store/vaccineDashboardStore';
import { HighlightableKPI } from './HighlightableKPI';

import { Skeleton } from './Skeleton';
import { KPIExplanationModal } from './KPIExplanationModal';

interface VaccineDashboardKPIsProps {
  model: VaccineOutputs | null;
  dashboardSettings: DashboardSettings;
  onSettingsChange: (settings: Partial<DashboardSettings>) => void;
}

export const VaccineDashboardKPIs: React.FC<VaccineDashboardKPIsProps> = ({ model, dashboardSettings, onSettingsChange }) => {
  const [viewMode, setViewMode] = useState<'incremental' | 'absolute'>('incremental');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [highlightedKPI, setHighlightedKPI] = useState<string | null>(null);
  const [selectedKPI, setSelectedKPI] = useState<string | null>(null);
  
  const backendResults = useVaccineDashboardStore((state) => state.backendResults);
  const backendComputedAt = useVaccineDashboardStore((state) => state.backendComputedAt);
  const isUsingBackend = backendResults && backendComputedAt && (Date.now() - backendComputedAt) < 5 * 60 * 1000;

  const formatNumber = (n: number | undefined | null) => {
    if (n === undefined || n === null || isNaN(n)) return '0';
    return Math.round(n).toLocaleString('fi-FI');
  };
  const formatDecimals = (n: number | undefined | null, decimals: number = 1) => {
    if (n === undefined || n === null || isNaN(n)) return '0';
    return Number(n).toFixed(decimals);
  };

  const { visibleKPIs } = dashboardSettings;

  const handleKPIClick = (kpiId: string) => {
    setHighlightedKPI(highlightedKPI === kpiId ? null : kpiId);
  };

  const handleInfoClick = (kpiId: string) => {
    setSelectedKPI(kpiId);
  };

  if (!model) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Skeleton width="120px" height="36px" borderRadius="6px" />
            <Skeleton width="100px" height="36px" borderRadius="6px" />
          </div>
          <Skeleton width="100px" height="36px" borderRadius="6px" />
        </div>
        <div className="vaccine-demo__kpi-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="vaccine-demo__kpi-card" style={{ height: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
               <Skeleton width="60%" height="16px" style={{ marginBottom: '12px' }} />
               <Skeleton width="80%" height="32px" />
            </div>
          ))}
        </div>
      </div>
    );
  }


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* View Mode Toggle and Settings Button */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setViewMode('incremental')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: viewMode === 'incremental' ? '#3b82f6' : '#475569',
                background: viewMode === 'incremental' ? '#1e40af' : 'transparent',
                color: '#f1f5f9',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Incremental (Δ)
            </button>
            <button
              onClick={() => setViewMode('absolute')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: viewMode === 'absolute' ? '#3b82f6' : '#475569',
                background: viewMode === 'absolute' ? '#1e40af' : 'transparent',
                color: '#f1f5f9',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Absolute
            </button>
          </div>
          
          {/* Backend Results Badge */}
          {isUsingBackend && (
            <div style={{
              padding: '4px 12px',
              borderRadius: '12px',
              background: '#10b981',
              color: '#fff',
              fontSize: '12px',
              fontWeight: '600',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Backend Results
            </div>
          )}
        </div>
        
        <button
          onClick={() => setIsSettingsOpen(true)}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid #475569',
            background: 'transparent',
            color: '#f1f5f9',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
          title="Configure visible KPIs"
        >
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
          </svg>
          Settings
        </button>
      </div>

      {/* Dashboard Settings Modal */}
      <DashboardSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={dashboardSettings}
        onSettingsChange={onSettingsChange}
      />

      {/* KPI Grid */}
      {viewMode === 'incremental' ? (
        // Incremental View (Δ = Vaccinate - No Vaccinate)
        <div className="vaccine-demo__kpi-grid vaccine-demo__fade-in">
          {/* Big 4 KPIs */}
          {visibleKPIs.icer && (

            <HighlightableKPI kpiId="icer" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>

              <KPICard

                label="ICER"

                value={model.icer === Infinity ? '∞' : formatEurosCompact(model.icer)}

                hint="Incremental cost per QALY gained (EUR/QALY)"

              />

            </HighlightableKPI>

          )}
          {visibleKPIs.qalysGained && (
            <HighlightableKPI kpiId="qalysGained" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
              <KPICard
                label="QALYs gained"
                value={formatNumber(model.incremental.qalyGained)}
                hint="Quality-adjusted life-years gained"
              />
            </HighlightableKPI>
          )}
          {visibleKPIs.budgetImpact && (
            <HighlightableKPI kpiId="budgetImpact" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
              <KPICard
                label="Budget Impact"
                value={formatEurosCompact(model.budgetImpact)}
                hint={model.budgetImpact < 0 ? 'Net cost savings' : 'Net budget increase'}
              />
            </HighlightableKPI>
          )}
          {visibleKPIs.bcr && (
            <HighlightableKPI kpiId="bcr" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
              <KPICard
                label="BCR"
                value={formatDecimals(model.bcr, 2)}
                hint="Benefit-cost ratio (savings/programme cost)"
              />
            </HighlightableKPI>
          )}
          
          {/* Additional Details */}
          {(visibleKPIs.lifeYearsGained || visibleKPIs.deathsAverted || visibleKPIs.casesAvoided || visibleKPIs.hospitalizationsAvoided) && (
            <>
              {visibleKPIs.lifeYearsGained && (
                <HighlightableKPI kpiId="lifeYearsGained" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Life-years gained"
                    value={formatNumber(model.incremental.lifeYearsGained)}
                    hint="Additional life-years from vaccination"
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.deathsAverted && (
                <HighlightableKPI kpiId="deathsAverted" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Deaths averted"
                    value={formatNumber(model.incremental.deathsAverted)}
                    hint="Lives saved by vaccination"
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.casesAvoided && (
                <HighlightableKPI kpiId="casesAvoided" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Cases avoided"
                    value={formatNumber(model.incremental.casesAvoided)}
                    hint="Infections prevented"
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.hospitalizationsAvoided && (
                <HighlightableKPI kpiId="hospitalizationsAvoided" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Hospitalizations avoided"
                    value={formatNumber(model.incremental.hospitalizationsAvoided)}
                    hint="Hospital admissions prevented"
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.adverseEventCost && (
                <HighlightableKPI kpiId="adverseEventCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Adverse event costs"
                    value={formatEurosCompact(model.costBreakdown.adverseEventCost)}
                    hint="Costs due to adverse events"
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.productivityCost && (
                <HighlightableKPI kpiId="productivitySaved" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Productivity savings"
                    value={formatEurosCompact(model.productivitySaved)}
                    hint="Societal savings from avoided lost work"
                  />
                </HighlightableKPI>
              )}
            </>
          )}
        </div>
      ) : (
        // Absolute View (Separate strategies)
        <div>
          {/* No Vaccinate Strategy */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px', fontFamily: 'Inter, sans-serif' }}>
              No Vaccinate Strategy
            </h3>
            <div className="vaccine-demo__kpi-grid vaccine-demo__fade-in">
              {visibleKPIs.lifeYearsGained && (
                <HighlightableKPI kpiId="noVax-lifeYears" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Life-years"
                    value={formatNumber(model.noVaccinate.lifeYears)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.qalysGained && (
                <HighlightableKPI kpiId="noVax-qalys" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="QALYs"
                    value={formatNumber(model.noVaccinate.qalys)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.deathsAverted && (
                <HighlightableKPI kpiId="noVax-deaths" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Deaths"
                    value={formatNumber(model.noVaccinate.deaths)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.casesAvoided && (
                <HighlightableKPI kpiId="noVax-cases" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Cases"
                    value={formatNumber(model.noVaccinate.cases)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.hospitalizationsAvoided && (
                <HighlightableKPI kpiId="noVax-hospitalizations" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Hospitalizations"
                    value={formatNumber(model.noVaccinate.hospitalizations)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.budgetImpact && (
                <>
                  <HighlightableKPI kpiId="noVax-healthcareCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                    <KPICard
                      label="Healthcare costs"
                      value={formatEurosCompact(model.noVaccinate.healthcareCost)}
                    />
                  </HighlightableKPI>
                  <HighlightableKPI kpiId="noVax-totalCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                    <KPICard
                      label="Total direct costs"
                      value={formatEurosCompact(model.noVaccinate.totalCost)}
                    />
                  </HighlightableKPI>
                </>
              )}
              {visibleKPIs.productivityCost && (
                <HighlightableKPI kpiId="noVax-indirectCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Indirect costs"
                    value={formatEurosCompact(model.noVaccinate.indirectCost)}
                  />
                </HighlightableKPI>
              )}
            </div>
          </div>

          {/* Vaccinate Strategy */}
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px', fontFamily: 'Inter, sans-serif' }}>
              Vaccinate Strategy
            </h3>
            <div className="vaccine-demo__kpi-grid vaccine-demo__fade-in">
              {visibleKPIs.lifeYearsGained && (
                <HighlightableKPI kpiId="vax-lifeYears" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Life-years"
                    value={formatNumber(model.vaccinate.lifeYears)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.qalysGained && (
                <HighlightableKPI kpiId="vax-qalys" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="QALYs"
                    value={formatNumber(model.vaccinate.qalys)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.deathsAverted && (
                <HighlightableKPI kpiId="vax-deaths" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Deaths"
                    value={formatNumber(model.vaccinate.deaths)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.casesAvoided && (
                <HighlightableKPI kpiId="vax-cases" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Cases"
                    value={formatNumber(model.vaccinate.cases)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.hospitalizationsAvoided && (
                <HighlightableKPI kpiId="vax-hospitalizations" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Hospitalizations"
                    value={formatNumber(model.vaccinate.hospitalizations)}
                  />
                </HighlightableKPI>
              )}
              {visibleKPIs.budgetImpact && (
                <>
                  <HighlightableKPI kpiId="vax-programmeCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                    <KPICard
                      label="Programme cost"
                      value={formatEurosCompact(model.vaccinate.programmeCost)}
                      hint="Vaccine + administration + adverse event costs"
                    />
                  </HighlightableKPI>
                  <HighlightableKPI kpiId="vax-healthcareCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                    <KPICard
                      label="Healthcare costs"
                      value={formatEurosCompact(model.vaccinate.healthcareCost)}
                    />
                  </HighlightableKPI>
                  {visibleKPIs.adverseEventCost && (
                    <HighlightableKPI kpiId="vax-adverseEventCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                      <KPICard
                        label="Adverse event costs"
                        value={formatEurosCompact(model.costBreakdown.adverseEventCost)}
                        hint="Costs due to adverse events"
                      />
                    </HighlightableKPI>
                  )}
                  <HighlightableKPI kpiId="vax-totalCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                    <KPICard
                      label="Total direct costs"
                      value={formatEurosCompact(model.vaccinate.totalCost)}
                      hint="Includes programme, healthcare, and adverse event costs"
                    />
                  </HighlightableKPI>
                </>
              )}
              {visibleKPIs.productivityCost && (
                <HighlightableKPI kpiId="vax-indirectCost" highlightedKPI={highlightedKPI} onClick={handleKPIClick} onInfoClick={handleInfoClick}>
                  <KPICard
                    label="Indirect costs"
                    value={formatEurosCompact(model.vaccinate.indirectCost)}
                  />
                </HighlightableKPI>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPI Explanation Modal */}
      <KPIExplanationModal
        kpiType={selectedKPI}
        model={model}
        onClose={() => setSelectedKPI(null)}
      />
    </div>
  );
};

